'use client';

import CardComponent, { type CardProps as CardComponentProps } from '@pixum/card';
import { ImageLoading } from '@pixum/logo';
import clsx from 'clsx';
import type { LinkNode } from 'data-layer';
import { CONTENTFUL_CARD_TYPES, CONTENTFUL_MOBILE_PRESENTATION_TYPES } from 'data-layer/constants';
import { nth } from 'utils';
import { buildIconAddonsFromFeatureFlag } from '../../../utils/icon/buildIconAddonsFromFeatureFlag';
import { Image } from '../../image';
import { IMAGE_TRIM } from '../../image/constants';
import { type LinkProps, withLinkMasking, type WrappedComponent } from '../../link';
import { RichTextBody } from '../../richtText';
import { isRichTextEmpty } from '../../richtText/utils/isRichTextEmpty';
import type { CardVariantProps } from '../types';

type CardWithLinkMaskingProps = CardComponentProps & LinkProps;
/**
 * Card component wrapped with link masking functionality. This allows the entire card to be clickable
 * while still supporting individual link behaviors within the card.
 */
export const CardWithLinkMasking = withLinkMasking<CardWithLinkMaskingProps>(
  CardComponent as WrappedComponent<CardWithLinkMaskingProps>
);

/*
 * The NavigationCard component is responsible for rendering a navigation card
 * with a headline, body, media, and a primary call-to-action (CTA) link.
 *
 * Current Comments:
 * - @todo handle the logic in CardContainer.tsx like Masked and App link logic
 * - @todo check why do we need to set undefined if no text. change in the component lib
 * - @todo need to remove key prop from component lib
 *
 * Props:
 * - card: An object containing the card data, including id, headline, CTA, images, and text.
 * - isMobile: A boolean indicating whether the component is being rendered in a mobile context.
 *
 * Key Features:
 * - Dynamically selects the appropriate image (desktop or mobile) based on the isMobile prop.
 * - Uses the RichTextBody component to render the card's text content.
 * - Constructs a media element using the Image component with lazy loading.
 * - Generates a Card component with various props such as body, href, id, media, and headline.
 *
 * Notes:
 * - The href is derived from the primary CTA's URL or defaults to an empty string if not provided.
 * - The textAlignment is hardcoded to "left" for consistent alignment across cards.
 */
export function NavigationCard(props: CardVariantProps) {
  const { id, headline, links, image, wrapperTag, text, className, featureFlag, device, supportingText, layout } =
    props;
  const link = nth(links, 0, {} as LinkNode);
  const body = !isRichTextEmpty(text) ? <RichTextBody text={text} /> : undefined;
  const href = link?.url || '';
  const masked = link?.masked || false;
  const mediaAddons = buildIconAddonsFromFeatureFlag(featureFlag);

  const media = <Image image={image} loading={ImageLoading.Lazy} device={device} trim={IMAGE_TRIM.NONE} />;

  return (
    <CardWithLinkMasking
      as={wrapperTag}
      className={clsx('navigation-card', className)}
      body={body}
      clickable={!!href}
      href={href}
      id={id}
      media={media}
      variant={CONTENTFUL_CARD_TYPES.NAVIGATION}
      headline={headline}
      textAlignment="left"
      mediaAddons={mediaAddons}
      masked={masked}
      isMiniCard={layout === CONTENTFUL_MOBILE_PRESENTATION_TYPES.TWO_COLUMN_GRID}
      supportingText={supportingText}
    />
  );
}
